// Radha — Disputes data, straight from PostgREST. Second module, same template.
//
// This is the whole cost of a new module's read path: copy useCollectionsDirect,
// change the table names and the column lists. RLS (migration 018) already scopes
// the tenant; there is no tenant id in any query here, by design.
import { useQuery } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

export type DisputeStatus =
  | "open" | "under_review" | "escalated" | "resolved" | "withdrawn";
export type DisputeVerdict = "customer_favored" | "company_favored" | "partial" | null;
export type HealthBand = "healthy" | "watch" | "at_risk" | "critical";

export interface Dispute {
  id: string;
  customer_name: string;
  customer_gstin: string | null;
  invoice_number: string;
  invoice_amount: string;
  disputed_amount: string;
  category: string;
  status: DisputeStatus;
  verdict: DisputeVerdict;
  verdict_confidence: string | null;
  raised_on: string;
  resolved_on: string | null;
  is_high_value: boolean;
}

export interface CustomerHealth {
  id: string;
  customer_name: string;
  health_score: string;
  churn_probability: string;
  open_disputes: number;
  health_band: HealthBand;
  last_scored_on: string;
}

export const disputesKeys = {
  disputes: ["disputes-direct", "cases"] as const,
  health: ["disputes-direct", "health"] as const,
};

export function useDisputes() {
  return useQuery({
    queryKey: disputesKeys.disputes,
    queryFn: async (): Promise<Dispute[]> => {
      const { data, error } = await supabase
        .from("disputes")
        .select(
          "id, customer_name, customer_gstin, invoice_number, invoice_amount, " +
            "disputed_amount, category, status, verdict, verdict_confidence, " +
            "raised_on, resolved_on, is_high_value",
        )
        .order("disputed_amount", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as Dispute[];
    },
    staleTime: 30_000,
  });
}

export function useCustomerHealth() {
  return useQuery({
    queryKey: disputesKeys.health,
    queryFn: async (): Promise<CustomerHealth[]> => {
      const { data, error } = await supabase
        .from("customer_health_scores")
        .select(
          "id, customer_name, health_score, churn_probability, " +
            "open_disputes, health_band, last_scored_on",
        )
        .order("churn_probability", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as CustomerHealth[];
    },
    staleTime: 30_000,
  });
}
